import { useLang } from '../contexts/LanguageContext';
import { ArrowLeft, FileText, AlertTriangle, CreditCard, Ban, Scale, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TermsOfService() {
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="terms-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-10">
        <Link to="/" className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#FADB5F] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0A3A82]/20 flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#FADB5F]" />
          </div>
          <h1 className="text-3xl font-bold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Terms of Service
          </h1>
        </div>

        <div className="space-y-8 text-sm text-[#94A3B8] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <p className="text-xs text-[#475569]">Last updated: April 2026</p>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>1. General Terms</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>EuroMoto is a European vehicle marketplace platform connecting buyers and sellers.</li>
              <li>By registering, you agree to these terms and our Privacy Policy.</li>
              <li>You must be at least 18 years old to use this platform.</li>
              <li>You are responsible for all activity under your account.</li>
              <li>EuroMoto operates across all European countries.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>2. Listings & Content</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>Sellers must provide accurate and truthful vehicle information.</li>
              <li>Misleading descriptions, false mileage, or fraudulent listings are strictly prohibited.</li>
              <li>EuroMoto reserves the right to remove any listing that violates these terms.</li>
              <li>Vehicle images must be of the actual vehicle being sold.</li>
              <li>Free listings remain active until manually removed by the seller.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>3. Payments & Premium Listings</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>Premium (featured) listings cost <strong className="text-[#FADB5F]">5.00 EUR per month</strong>.</li>
              <li>All payments are processed securely through <strong className="text-[#F1F5F9]">Stripe</strong>, a PCI DSS Level 1 certified payment processor.</li>
              <li>EuroMoto does not store or have access to your credit card details.</li>
              <li>Featured listings appear at the top of search results for 30 days from payment.</li>
              <li>Payments are non-refundable once the featuring period has begun.</li>
              <li>All prices include applicable taxes.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Ban className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Prohibited Activities</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>Posting stolen vehicles or vehicles without legal ownership documentation.</li>
              <li>Spam, duplicate listings, or automated posting.</li>
              <li>Harassment, threats, or abusive language in messages.</li>
              <li>Attempting to bypass platform fees or security measures.</li>
              <li>Using the platform for money laundering or other illegal activities.</li>
              <li>Impersonating other users or organizations.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Liability & Disputes</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>EuroMoto is a marketplace platform and is not a party to transactions between buyers and sellers.</li>
              <li>We do not guarantee the accuracy of listings or the condition of vehicles.</li>
              <li>Buyers are advised to inspect vehicles in person before purchase.</li>
              <li>Disputes between buyers and sellers should be resolved directly between the parties.</li>
              <li>EuroMoto may assist in mediation but is not obligated to do so.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Contact</h2>
            </div>
            <p>For questions about these terms:</p>
            <p className="mt-2">Email: <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
            <p>EuroMoto — European Vehicle Marketplace</p>
          </section>
        </div>
      </div>
    </div>
  );
}
